import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useChatNickname, { validateMessage } from './useChatNickname';
import {
  ensureDefaultTopics,
  subscribeTopics,
  subscribeMessages,
  createTopic,
  sendMessage,
} from './chatService';

function ChatNicknameModal({ onSave, onClose, initialValue = '' }) {
  const { t } = useTranslation();
  const [input, setInput] = useState(initialValue);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = onSave(input);
    if (err) setError(err);
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <h2 style={{ margin: '0 0 8px', color: '#333', fontSize: 20 }}>
          {t('chat.setNickname')}
        </h2>
        <p style={{ margin: '0 0 16px', color: '#666', fontSize: 14 }}>
          {t('chat.nicknameHint')}
        </p>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={input}
            onChange={(e) => { setInput(e.target.value); setError(''); }}
            placeholder={t('chat.nicknamePlaceholder')}
            maxLength={20}
            autoFocus
            style={inputStyle}
          />
          {error && <p style={{ color: '#d32f2f', fontSize: 13, margin: '8px 0 0' }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button type="submit" style={primaryBtnStyle}>
              {t('chat.saveNickname')}
            </button>
            {onClose && (
              <button type="button" onClick={onClose} style={secondaryBtnStyle}>
                {t('chat.cancel')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function formatTime(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export default function PublicChat({ user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { topicId: urlTopicId } = useParams();
  const visitorId = user?.uid || '';
  const suggestedNick = user?.displayName || '';
  const {
    nickname, showNicknameModal, setShowNicknameModal, saveNickname, hasNickname,
  } = useChatNickname();

  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(urlTopicId || null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [messageError, setMessageError] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [newTopicDesc, setNewTopicDesc] = useState('');
  const [topicError, setTopicError] = useState('');
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const messagesEndRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  useEffect(() => {
    ensureDefaultTopics();
    const unsub = subscribeTopics((data) => {
      setTopics(data);
      setLoadingTopics(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (urlTopicId) setSelectedTopicId(urlTopicId);
  }, [urlTopicId]);

  useEffect(() => {
    if (!selectedTopicId) {
      setMessages([]);
      return undefined;
    }
    const unsub = subscribeMessages(selectedTopicId, setMessages);
    return unsub;
  }, [selectedTopicId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectTopic = (id) => {
    setSelectedTopicId(id);
    navigate(`/chat/${id}`, { replace: true });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!hasNickname) {
      setShowNicknameModal(true);
      return;
    }
    const err = validateMessage(messageText);
    if (err) {
      setMessageError(err);
      return;
    }
    setSending(true);
    setMessageError('');
    try {
      await sendMessage({
        topicId: selectedTopicId,
        text: messageText,
        nickname,
        visitorId,
      });
      setMessageText('');
    } catch {
      setMessageError(t('chat.sendError'));
    } finally {
      setSending(false);
    }
  };

  const handleCreateTopic = async (e) => {
    e.preventDefault();
    if (!hasNickname) {
      setShowNicknameModal(true);
      return;
    }
    const title = newTopicTitle.trim();
    if (title.length < 3 || title.length > 60) {
      setTopicError(t('chat.topicTitleError'));
      return;
    }
    setCreatingTopic(true);
    setTopicError('');
    try {
      const id = await createTopic({
        title,
        description: newTopicDesc,
        nickname,
        visitorId,
      });
      setNewTopicTitle('');
      setNewTopicDesc('');
      setShowNewTopic(false);
      selectTopic(id);
    } catch {
      setTopicError(t('chat.createTopicError'));
    } finally {
      setCreatingTopic(false);
    }
  };

  const selectedTopic = topics.find((tp) => tp.id === selectedTopicId);

  return (
    <div style={pageStyle}>
      {showNicknameModal && (
        <ChatNicknameModal
          onSave={saveNickname}
          onClose={() => setShowNicknameModal(false)}
          initialValue={suggestedNick}
        />
      )}

      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: isMobile ? 20 : 24, color: '#333' }}>
            💬 {t('chat.title')}
          </h1>
        </div>
        {hasNickname && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, color: '#666' }}>
              {t('chat.yourNickname')}: <strong style={{ color: '#ff9800' }}>{nickname}</strong>
            </span>
            <button
              type="button"
              onClick={() => setShowNicknameModal(true)}
              style={changeNickBtnStyle}
            >
              {t('chat.changeNickname')}
            </button>
          </div>
        )}
      </div>

      <p style={{ margin: '0 0 16px', color: '#888', fontSize: 13, textAlign: 'center' }}>
        {t('chat.publicNotice')}
      </p>

      <div style={layoutStyle(isMobile)}>
        {/* Panel tematów */}
        <aside style={sidebarStyle(isMobile, !!selectedTopicId && isMobile)}>
          <div style={sidebarHeaderStyle}>
            <h2 style={{ margin: 0, fontSize: 16, color: '#333' }}>{t('chat.topics')}</h2>
            <button
              type="button"
              onClick={() => setShowNewTopic(!showNewTopic)}
              style={newTopicBtnStyle}
            >
              + {t('chat.newTopic')}
            </button>
          </div>

          {showNewTopic && (
            <form onSubmit={handleCreateTopic} style={newTopicFormStyle}>
              <input
                type="text"
                value={newTopicTitle}
                onChange={(e) => setNewTopicTitle(e.target.value)}
                placeholder={t('chat.topicTitlePlaceholder')}
                maxLength={60}
                style={inputStyle}
              />
              <input
                type="text"
                value={newTopicDesc}
                onChange={(e) => setNewTopicDesc(e.target.value)}
                placeholder={t('chat.topicDescPlaceholder')}
                maxLength={120}
                style={{ ...inputStyle, marginTop: 8 }}
              />
              {topicError && <p style={{ color: '#d32f2f', fontSize: 12, margin: '6px 0 0' }}>{topicError}</p>}
              <button type="submit" disabled={creatingTopic} style={{ ...primaryBtnStyle, marginTop: 8, width: '100%' }}>
                {creatingTopic ? '...' : t('chat.createTopic')}
              </button>
            </form>
          )}

          {loadingTopics ? (
            <p style={{ color: '#999', fontSize: 14, padding: 12 }}>{t('chat.loading')}</p>
          ) : (
            <ul style={topicListStyle}>
              {topics.map((topic) => (
                <li key={topic.id}>
                  <button
                    type="button"
                    onClick={() => selectTopic(topic.id)}
                    style={topicBtnStyle(selectedTopicId === topic.id)}
                  >
                    <span style={{ fontWeight: 600 }}>{topic.title}</span>
                    {topic.description && (
                      <span style={{ display: 'block', fontSize: 12, color: '#888', marginTop: 2 }}>
                        {topic.description}
                      </span>
                    )}
                    {topic.messageCount > 0 && (
                      <span style={{ fontSize: 11, color: '#ff9800', marginTop: 2, display: 'block' }}>
                        {topic.messageCount} {t('chat.messages')}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Panel wiadomości */}
        <main style={mainPanelStyle(isMobile, !selectedTopicId && isMobile)}>
          {!selectedTopicId ? (
            <div style={emptyStateStyle}>
              <span style={{ fontSize: 48 }}>💬</span>
              <p style={{ color: '#666', marginTop: 12 }}>{t('chat.selectTopic')}</p>
            </div>
          ) : (
            <>
              <div style={topicHeaderStyle}>
                {isMobile && (
                  <button type="button" onClick={() => setSelectedTopicId(null)} style={backBtnStyle}>
                    ← {t('chat.backToTopics')}
                  </button>
                )}
                <h2 style={{ margin: isMobile ? '8px 0 0' : 0, fontSize: 18, color: '#333' }}>
                  {selectedTopic?.title || '...'}
                </h2>
                {selectedTopic?.description && (
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>{selectedTopic.description}</p>
                )}
              </div>

              <div style={messagesContainerStyle}>
                {messages.length === 0 ? (
                  <p style={{ color: '#999', textAlign: 'center', marginTop: 40 }}>
                    {t('chat.noMessages')}
                  </p>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      style={messageBubbleStyle(msg.visitorId === visitorId)}
                    >
                      <div style={messageHeaderStyle}>
                        <strong style={{ color: msg.visitorId === visitorId ? '#ff9800' : '#555' }}>
                          {msg.nickname}
                          {msg.visitorId === visitorId && ` (${t('chat.you')})`}
                        </strong>
                        <span style={{ fontSize: 11, color: '#aaa' }}>{formatTime(msg.createdAt)}</span>
                      </div>
                      <p style={{ margin: '4px 0 0', fontSize: 14, color: '#333', wordBreak: 'break-word' }}>
                        {msg.text}
                      </p>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleSendMessage} style={inputBarStyle(isMobile)}>
                <input
                  type="text"
                  value={messageText}
                  onChange={(e) => { setMessageText(e.target.value); setMessageError(''); }}
                  placeholder={hasNickname ? t('chat.messagePlaceholder') : t('chat.setNicknameFirst')}
                  maxLength={500}
                  disabled={!hasNickname}
                  style={{ ...inputStyle, ...(isMobile ? {} : { flex: 1 }) }}
                />
                <button
                  type="submit"
                  disabled={sending || !messageText.trim()}
                  style={{ ...sendBtnStyle, width: isMobile ? '100%' : 'auto', alignSelf: isMobile ? 'stretch' : 'flex-end' }}
                >
                  {sending ? '...' : t('chat.send')}
                </button>
              </form>
              {messageError && (
                <p style={{ color: '#d32f2f', fontSize: 12, margin: '4px 16px 0' }}>{messageError}</p>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #fffde7 0%, #fff 50%, #fff8e1 100%)',
  padding: '16px',
  boxSizing: 'border-box',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 12,
  marginBottom: 8,
  maxWidth: 1200,
  margin: '0 auto 8px',
};

const layoutStyle = (isMobile) => ({
  display: 'flex',
  gap: 16,
  maxWidth: 1200,
  margin: '0 auto',
  height: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 140px)',
  minHeight: 400,
});

const sidebarStyle = (isMobile, hidden) => ({
  width: isMobile ? '100%' : 280,
  flexShrink: 0,
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #eee',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  display: hidden ? 'none' : 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const sidebarHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  borderBottom: '1px solid #eee',
};

const mainPanelStyle = (isMobile, hidden) => ({
  flex: 1,
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #eee',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  display: hidden ? 'none' : 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minWidth: 0,
});

const topicListStyle = {
  listStyle: 'none',
  margin: 0,
  padding: '8px',
  overflowY: 'auto',
  flex: 1,
};

const topicBtnStyle = (active) => ({
  width: '100%',
  textAlign: 'left',
  padding: '10px 12px',
  border: 'none',
  borderRadius: 8,
  background: active ? 'linear-gradient(90deg, #fff8e1, #fffde7)' : 'transparent',
  borderLeft: active ? '3px solid #ff9800' : '3px solid transparent',
  cursor: 'pointer',
  marginBottom: 4,
  transition: 'background 0.15s',
});

const messagesContainerStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '12px 16px',
};

const messageBubbleStyle = (isOwn) => ({
  background: isOwn ? '#fff8e1' : '#f5f5f5',
  borderRadius: 10,
  padding: '8px 12px',
  marginBottom: 8,
  borderLeft: isOwn ? '3px solid #ff9800' : '3px solid #ddd',
});

const messageHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
};

const topicHeaderStyle = {
  padding: '12px 16px',
  borderBottom: '1px solid #eee',
};

const inputBarStyle = (isMobile) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 8,
  padding: '12px 16px',
  borderTop: '1px solid #eee',
  ...(isMobile ? {} : { alignItems: 'center' }),
});

const emptyStateStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  color: '#999',
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: 16,
};

const modalStyle = {
  background: '#fff',
  borderRadius: 16,
  padding: 24,
  maxWidth: 400,
  width: '100%',
  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '2px solid #ddd',
  borderRadius: 8,
  fontSize: 14,
  boxSizing: 'border-box',
  outline: 'none',
};

const primaryBtnStyle = {
  background: 'linear-gradient(90deg, #ff9800, #ffb300)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryBtnStyle = {
  background: '#f5f5f5',
  color: '#666',
  border: 'none',
  borderRadius: 8,
  padding: '10px 20px',
  fontSize: 14,
  cursor: 'pointer',
};

const sendBtnStyle = {
  ...primaryBtnStyle,
  padding: '12px 16px',
  whiteSpace: 'nowrap',
};

const backBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#ff9800',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  padding: 0,
};

const changeNickBtnStyle = {
  background: 'none',
  border: '1px solid #ff9800',
  color: '#ff9800',
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 12,
  cursor: 'pointer',
};

const newTopicBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#ff9800',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const newTopicFormStyle = {
  padding: '8px 12px 12px',
  borderBottom: '1px solid #eee',
};
